import { Link, useRouterState } from '@tanstack/react-router';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  alpha,
} from '@mui/material';
// ⚡ Path-based imports (6× faster than barrel imports)
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SearchIcon from '@mui/icons-material/Search';
import { VicViperIcon } from '@/components/icons/VicViperIcon';
import { SpotifyIcon } from '@/components/icons/SpotifyIcon';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { getAccentColor, getTextGradient, getPageColors } from '@/theme/colors';
import { useThemeContext } from '@/contexts/ThemeContext';
import { usePageColors } from '@/contexts/PageColorContext';
import { hasKonamiCode } from '@/lib/achievements';
import { analytics } from '@/lib/analytics';
import { setSiteHeaderHeight } from '@/lib/siteHeader';

const ALL_NAV_ITEMS = [
  { label: 'Home', path: '/' },
  { label: 'Blog', path: '/blog' },
  { label: 'Music', path: '/music' },
  { label: 'ProdEng', path: '/prodeng' },
  { label: 'Projects', path: '/projects' },
  { label: 'Uses', path: '/uses' },
  { label: 'About', path: '/about' },
  { label: 'Quotes', path: '/quotes' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hasAchievement, setHasAchievement] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);
  const [visibleCount, setVisibleCount] = useState(ALL_NAV_ITEMS.length);
  const headerRef = useRef<HTMLElement>(null);
  const navContainerRef = useRef<HTMLDivElement>(null);
  const navItemWidths = useRef<number[]>([]);
  const router = useRouterState();
  const currentPath = router.location.pathname;
  const { mode, isRetro, toggleMode } = useThemeContext();

  const { colors: currentPageColors } = usePageColors();
  const logoGradient = useMemo(
    () => isRetro
      ? 'linear-gradient(135deg, #00ff41 0%, #00cc33 100%)'
      : getTextGradient(currentPageColors, mode),
    [currentPageColors, mode, isRetro],
  );

  useEffect(() => {
    setHasAchievement(hasKonamiCode());
    const handler = () => setHasAchievement(true);
    window.addEventListener('achievement-unlocked', handler);
    return () => window.removeEventListener('achievement-unlocked', handler);
  }, []);

  const handleDrawerToggle = () => {
    const next = !mobileOpen;
    analytics.drawerToggled(next);
    setMobileOpen(next);
  };

  const navItems = ALL_NAV_ITEMS.slice(0, visibleCount);
  const overflowItems = ALL_NAV_ITEMS.slice(visibleCount);

  const OVERFLOW_BTN_WIDTH = 40;
  const NAV_GAP = 8;

  const measureAndFit = useCallback(() => {
    const container = navContainerRef.current;
    if (!container) return;

    const buttons = container.querySelectorAll<HTMLElement>('[data-nav-item]');
    if (buttons.length === 0 && navItemWidths.current.length === 0) return;

    if (buttons.length > 0) {
      navItemWidths.current = Array.from(buttons).map(
        (btn) => btn.getBoundingClientRect().width + NAV_GAP,
      );
    }

    const containerWidth = container.getBoundingClientRect().width;
    const widths = navItemWidths.current;
    if (widths.length === 0) return;

    let usedWidth = 0;
    let fits = 0;

    for (let i = 0; i < ALL_NAV_ITEMS.length; i++) {
      const w = widths[i] ?? widths[widths.length - 1];
      const needsOverflow = i < ALL_NAV_ITEMS.length - 1;
      const reservedForOverflow = needsOverflow ? OVERFLOW_BTN_WIDTH : 0;

      if (usedWidth + w + reservedForOverflow <= containerWidth) {
        usedWidth += w;
        fits++;
      } else {
        break;
      }
    }

    setVisibleCount((prev) => {
      const next = Math.max(fits, 2);
      return next !== prev ? next : prev;
    });
  }, []);

  useEffect(() => {
    const container = navContainerRef.current;
    if (!container) return;

    const ro = new ResizeObserver(() => measureAndFit());
    ro.observe(container);
    measureAndFit();

    return () => ro.disconnect();
  }, [measureAndFit]);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const apply = () => setSiteHeaderHeight(el.getBoundingClientRect().height);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const isActive = (path: string) => {
    if (path === '/') {
      return currentPath === '/';
    }
    return currentPath.startsWith(path);
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Branded header */}
      <Box
        sx={{
          background: currentPageColors.gradient,
          px: 2.5,
          pt: 2,
          pb: 2.5,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.02em',
              lineHeight: 1,
            }}
          >
            CB
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.7)',
              fontSize: '0.65rem',
              letterSpacing: '0.5px',
              mt: 0.5,
              display: 'block',
            }}
          >
            Developer · Builder · Creator
          </Typography>
        </Box>
        <IconButton
          onClick={handleDrawerToggle}
          aria-label="Close menu"
          size="small"
          sx={{
            color: 'rgba(255,255,255,0.8)',
            mt: -0.5,
            mr: -0.5,
            '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.15)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Nav items */}
      <List sx={{ flex: 1, pt: 1, pb: 0 }}>
        {ALL_NAV_ITEMS.map((item) => {
          const active = isActive(item.path);
          const itemColors = getPageColors(item.path);
          const accentColor = mode === 'dark' ? itemColors.accent : itemColors.primary;

          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                {...(item.path === '/' ? { search: {} } : {})}
                onClick={() => { analytics.navigationClicked(item.path, 'mobile-drawer'); handleDrawerToggle(); }}
                sx={{
                  py: 1.25,
                  px: 2.5,
                  borderLeft: '3px solid',
                  borderColor: active ? accentColor : 'transparent',
                  bgcolor: active ? alpha(accentColor, 0.08) : 'transparent',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: alpha(accentColor, 0.06),
                    borderColor: active ? accentColor : alpha(accentColor, 0.3),
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: active ? 700 : 500,
                    fontSize: '0.95rem',
                    color: active ? accentColor : 'text.primary',
                    letterSpacing: active ? '-0.01em' : 'normal',
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Bottom section */}
      <Box sx={{ mt: 'auto' }}>
        <Divider />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              component="a"
              href="https://github.com/cdbattags"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
              size="small"
              onClick={() => analytics.outboundLinkClicked('https://github.com/cdbattags', 'mobile-drawer')}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' },
              }}
            >
              <GitHubIcon fontSize="small" />
            </IconButton>
            <IconButton
              component="a"
              href="https://linkedin.com/in/cdbattags"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              size="small"
              onClick={() => analytics.outboundLinkClicked('https://linkedin.com/in/cdbattags', 'mobile-drawer')}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: '#0077B5' },
              }}
            >
              <LinkedInIcon fontSize="small" />
            </IconButton>
            <IconButton
              component="a"
              href="https://open.spotify.com/user/1229186524"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Spotify"
              size="small"
              onClick={() => analytics.outboundLinkClicked('https://open.spotify.com/user/1229186524', 'mobile-drawer')}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: '#1DB954' },
              }}
            >
              <SpotifyIcon fontSize="small" />
            </IconButton>
          </Box>
          <ThemeToggle mode={mode} onToggle={toggleMode} />
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        ref={headerRef}
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: { xs: alpha(mode === 'dark' ? '#121212' : '#ffffff', 0.85), md: 'background.paper' },
          backdropFilter: { xs: 'blur(12px) saturate(180%)', md: 'none' },
          WebkitBackdropFilter: { xs: 'blur(12px) saturate(180%)', md: 'none' },
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 70 } }}>
            {/* Mobile Menu Icon */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                mr: 1,
                display: { xs: 'flex', md: 'none' },
                minWidth: 44,
                minHeight: 44,
              }}
            >
              <MenuIcon />
            </IconButton>

            {/* Logo/Brand */}
            <Box
              component={Link}
              to="/"
              search={{}}
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                color: 'inherit',
                flexGrow: { xs: 1, md: 0 },
                flexShrink: 0,
                minWidth: 0,
              }}
            >
              <Box sx={{ whiteSpace: 'nowrap' }}>
                {/* Mobile: Short version */}
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.2,
                    fontSize: '1.25rem',
                    letterSpacing: '-0.02em',
                    background: logoGradient,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    display: { xs: 'block', md: 'none' },
                  }}
                >
                  CB
                </Typography>

                {/* Desktop: Full version */}
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1.2,
                    fontSize: '1.05rem',
                    background: logoGradient,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    display: { xs: 'none', md: 'block' },
                  }}
                >
                  Christian Battaglia
                </Typography>

                {/* Desktop: Tagline */}
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.65rem',
                    letterSpacing: isRetro ? '0.15em' : '0.5px',
                    display: { xs: 'none', md: 'block' },
                    ...(isRetro && { textTransform: 'uppercase' }),
                  }}
                >
                  Developer · Builder · Creator
                </Typography>
              </Box>
            </Box>

            {/* Mobile Search Icon */}
            <IconButton
              aria-label="Search (⌘K)"
              onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}
              sx={{
                display: { xs: 'flex', md: 'none' },
                color: 'text.secondary',
                minWidth: 44,
                minHeight: 44,
              }}
            >
              <SearchIcon />
            </IconButton>

            {/* Desktop Navigation */}
            <Box
              ref={navContainerRef}
              sx={{
                flexGrow: 1,
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: `${NAV_GAP}px`,
                ml: 4,
                overflow: 'hidden',
              }}
            >
              {navItems.map((item) => {
                const active = isActive(item.path);
                const navColor = isRetro ? '#00ff41' : getAccentColor(item.path, mode);

                return (
                  <Button
                    key={item.path}
                    data-nav-item
                    color="inherit"
                    component={Link}
                    to={item.path}
                    {...(item.path === '/' ? { search: {} } : {})}
                    onClick={() => analytics.navigationClicked(item.path, 'header')}
                    sx={{
                      textTransform: 'none',
                      fontWeight: active ? 700 : 500,
                      fontSize: '0.9rem',
                      px: 1.5,
                      minWidth: 'auto',
                      flexShrink: 0,
                      position: 'relative',
                      color: active ? navColor : 'inherit',
                      ...(isRetro && active && {
                        bgcolor: 'rgba(0, 255, 65, 0.1)',
                      }),
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 6,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: active ? '60%' : '0%',
                        height: '2px',
                        bgcolor: navColor,
                        transition: 'width 0.3s ease',
                      },
                      '&:hover': {
                        color: navColor,
                        ...(isRetro && { bgcolor: 'rgba(0, 255, 65, 0.08)' }),
                      },
                      '&:hover::after': {
                        width: '60%',
                        bgcolor: navColor,
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}

              {overflowItems.length > 0 && (
                <>
                  <IconButton
                    size="small"
                    aria-label="More pages"
                    onClick={(e) => {
                      setMoreAnchor(e.currentTarget);
                      analytics.overflowMenuOpened();
                    }}
                    sx={{
                      flexShrink: 0,
                      color: moreAnchor || overflowItems.some((i) => isActive(i.path))
                        ? 'text.primary'
                        : 'text.secondary',
                    }}
                  >
                    <MoreHorizIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                  <Menu
                    anchorEl={moreAnchor}
                    open={Boolean(moreAnchor)}
                    onClose={() => setMoreAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{
                      paper: { sx: { minWidth: 140, mt: 0.5 } },
                    }}
                  >
                    {overflowItems.map((item) => {
                      const active = isActive(item.path);
                      const navColor = isRetro ? '#00ff41' : getAccentColor(item.path, mode);
                      return (
                        <MenuItem
                          key={item.path}
                          component={Link}
                          to={item.path}
                          {...(item.path === '/' ? { search: {} } : {})}
                          onClick={() => { setMoreAnchor(null); analytics.navigationClicked(item.path, 'overflow'); }}
                          selected={active}
                          sx={{
                            fontSize: '0.9rem',
                            fontWeight: active ? 700 : 400,
                            color: active ? navColor : 'inherit',
                          }}
                        >
                          {item.label}
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </>
              )}
            </Box>

            {/* Search, Theme Toggle & Social Icons */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, alignItems: 'center' }}>
              <Box
                component="button"
                onClick={() => {
                  analytics.searchOpened('button');
                  document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  bgcolor: 'transparent',
                  color: 'text.secondary',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: 'text.secondary',
                    color: 'text.primary',
                  },
                }}
              >
                <SearchIcon sx={{ fontSize: 16 }} />
                Search
                <Box
                  component="kbd"
                  sx={{
                    fontSize: '0.65rem',
                    px: 0.5,
                    py: 0.1,
                    borderRadius: 0.5,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                    lineHeight: 1.4,
                    ml: 0.5,
                  }}
                >
                  ⌘K
                </Box>
              </Box>
              {hasAchievement && (
                <IconButton
                  size="small"
                  aria-label="Achievement: Konami Code — Vic Viper (Gradius, 1986)"
                  title="↑↑↓↓←→←→BA"
                  sx={{
                    color: '#00ff41',
                    animation: 'viper-pulse 2s ease-in-out infinite alternate',
                    '@keyframes viper-pulse': {
                      '0%': { filter: 'drop-shadow(0 0 2px rgba(0,255,65,0.3))', transform: 'translateX(0)' },
                      '100%': { filter: 'drop-shadow(0 0 8px rgba(0,255,65,0.7))', transform: 'translateX(1px)' },
                    },
                  }}
                >
                  <VicViperIcon sx={{ fontSize: 22 }} />
                </IconButton>
              )}
              <ThemeToggle mode={mode} onToggle={toggleMode} />
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <IconButton
                component="a"
                href="https://github.com/cdbattags"
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                aria-label="GitHub"
                onClick={() => analytics.outboundLinkClicked('https://github.com/cdbattags', 'header')}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                <GitHubIcon fontSize="small" />
              </IconButton>
              <IconButton
                component="a"
                href="https://linkedin.com/in/cdbattags"
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                aria-label="LinkedIn"
                onClick={() => analytics.outboundLinkClicked('https://linkedin.com/in/cdbattags', 'header')}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: '#0077B5' },
                }}
              >
                <LinkedInIcon fontSize="small" />
              </IconButton>
              <IconButton
                component="a"
                href="https://open.spotify.com/user/1229186524"
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                aria-label="Spotify"
                onClick={() => analytics.outboundLinkClicked('https://open.spotify.com/user/1229186524', 'header')}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: '#1DB954' },
                }}
              >
                <SpotifyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            borderRight: 'none',
            boxShadow: '4px 0 24px rgba(0,0,0,0.12)',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
}
